/**
 * Events Calendar – calendar view with month grid and event listing.
 * Re-uses the same Storefront GraphQL endpoint and metaobject structure
 * as events-loader.js.
 */

class EventsCalendar {
  static MONTH_NAMES = [
    'Januari',
    'Februari',
    'Mars',
    'April',
    'Maj',
    'Juni',
    'Juli',
    'Augusti',
    'September',
    'Oktober',
    'November',
    'December',
  ];
  static MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
  static WEEKDAYS = ['MÅN', 'TIS', 'ONS', 'TOR', 'FRE', 'LÖR', 'SÖN'];

  static CATEGORY_COLORS = {
    tävlingar: '#e74c3c',
    kvällsaktivitet: '#8e44ad',
    filmvisning: '#2980b9',
    release: '#27ae60',
    workshop: '#f39c12',
    demodagar: '#2251c8',
    'after ski': '#d35400',
    community: '#1f8b13',
  };

  static DEFAULT_COLOR = 'rgb(var(--color-foreground))';

  constructor(root) {
    this.root = root;
    if (!this.root) return;

    this.apiEndpoint = '/api/2024-10/graphql.json';
    this.allEvents = [];
    this.activeCategories = new Set();
    this.today = new Date();
    this.currentYear = this.today.getFullYear();
    this.currentMonth = this.today.getMonth();

    this.init();
  }

  /* ─── DATA ─── */

  async init() {
    try {
      this.allEvents = await this.fetchEvents();
      this.collectCategories();
      this.render();
    } catch (err) {
      console.error('EventsCalendar: failed to load', err);
      this.root.innerHTML = '<p class="events-cal__loading">Det gick inte att ladda kalendern. Försök igen senare.</p>';
    }
  }

  async fetchEvents() {
    const query = `
      query GetAllEvents {
        metaobjects(type: "events", first: 250) {
          nodes {
            handle
            fields {
              key
              value
              reference {
                ... on MediaImage {
                  image {
                    url(transform: { maxWidth: 800 })
                    altText
                    width
                    height
                  }
                }
              }
            }
          }
        }
      }
    `;

    const res = await fetch(this.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.shopifyConfig?.storefrontAccessToken || '',
      },
      body: JSON.stringify({ query }),
    });

    if (!res.ok) throw new Error(res.statusText);
    const { data, errors } = await res.json();
    if (errors) throw new Error(errors[0].message);

    return data.metaobjects.nodes
      .map((node) => {
        const ev = { handle: node.handle };
        node.fields.forEach((f) => {
          if (f.key === 'rubrik') ev.title = f.value;
          if (f.key === 'starttid') ev.startDate = new Date(f.value);
          if (f.key === 'sluttid') ev.endDate = new Date(f.value);
          if (f.key === 'adress') ev.address = f.value;
          if (f.key === 'kategori') ev.categories = f.value ? JSON.parse(f.value) : [];
          if (f.key === 'huvudbild' && f.reference?.image) {
            ev.image = f.reference.image.url;
            ev.imageAlt = f.reference.image.altText || ev.title;
            ev.imageWidth = f.reference.image.width;
            ev.imageHeight = f.reference.image.height;
          }
        });
        return ev;
      })
      .filter((ev) => ev.startDate && ev.endDate);
  }

  collectCategories() {
    const cats = new Set();
    this.allEvents.forEach((ev) => ev.categories?.forEach((c) => cats.add(c)));
    this.allCategories = [...cats].sort();
    this.activeCategories = new Set(this.allCategories);
  }

  categoryColor(cat) {
    const slug = cat.toLowerCase().trim();
    return EventsCalendar.CATEGORY_COLORS[slug] || EventsCalendar.DEFAULT_COLOR;
  }

  /* ─── FILTERED DATA ─── */

  get filteredEvents() {
    if (this.activeCategories.size === this.allCategories.length) return this.allEvents;
    return this.allEvents.filter((ev) => ev.categories?.some((c) => this.activeCategories.has(c)));
  }

  eventsForMonth(year, month) {
    return this.filteredEvents
      .filter((ev) => {
        const first = new Date(year, month, 1);
        const last = new Date(year, month + 1, 0, 23, 59, 59);
        return ev.startDate <= last && ev.endDate >= first;
      })
      .sort((a, b) => a.startDate - b.startDate);
  }

  eventsForDay(year, month, day) {
    const dayStart = new Date(year, month, day, 0, 0, 0);
    const dayEnd = new Date(year, month, day, 23, 59, 59);
    return this.filteredEvents.filter((ev) => ev.startDate <= dayEnd && ev.endDate >= dayStart);
  }

  /* ─── RENDER MAIN ─── */

  render() {
    const events = this.eventsForMonth(this.currentYear, this.currentMonth);
    const eventCount = events.length;

    this.root.innerHTML = `
      <div class="events-cal__top-layout">
        <aside class="events-cal__filter-panel">
          <div class="events-filter-wrapper" id="ecal-tag-filters-wrapper" style="display: none">
            <div class="filter-options board-finder__swiper-container" id="ecal-tag-filters" aria-label="Filtrera på kategori">
              <div class="board-finder__swiper">
                <div class="board-finder__swiper-wrapper swiper-wrapper"></div>
                <div class="board-finder__swiper-button-next swiper-button-next"></div>
                <div class="board-finder__swiper-button-prev swiper-button-prev"></div>
              </div>
            </div>
          </div>
          <div class="events-cal__legend caption" id="ecal-legend"></div>
        </aside>

        <div class="events-cal__calendar-panel">
          <nav class="events-cal__nav">
            <button class="events-cal__nav-btn" id="ecal-prev" aria-label="Föregående månad">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <div class="events-cal__nav-center">
              <h2 class="events-cal__month-label h1">${EventsCalendar.MONTH_NAMES[this.currentMonth]}</h2>
              <span class="events-cal__year-count caption">${this.currentYear} &bull; ${eventCount} EVENT${eventCount !== 1 ? 'S' : ''}</span>
            </div>
            <button class="events-cal__nav-btn" id="ecal-next" aria-label="Nästa månad">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </nav>
          <div class="events-cal__grid" id="ecal-grid"></div>
        </div>
      </div>
      <div class="events-cal__list-header caption-with-letter-spacing" id="ecal-list-header"></div>
      <div class="events-cal__list" id="ecal-list"></div>
    `;

    this.renderFilter();
    this.renderLegend();
    this.renderGrid();
    this.renderList(events);

    this.root.querySelector('#ecal-prev').addEventListener('click', () => this.changeMonth(-1));
    this.root.querySelector('#ecal-next').addEventListener('click', () => this.changeMonth(1));
  }

  changeMonth(delta) {
    this.currentMonth += delta;
    if (this.currentMonth > 11) {
      this.currentMonth = 0;
      this.currentYear++;
    }
    if (this.currentMonth < 0) {
      this.currentMonth = 11;
      this.currentYear--;
    }
    this.updateView();
  }

  updateView() {
    const events = this.eventsForMonth(this.currentYear, this.currentMonth);
    const count = events.length;

    const label = this.root.querySelector('.events-cal__month-label');
    const yearCount = this.root.querySelector('.events-cal__year-count');
    if (label) label.textContent = EventsCalendar.MONTH_NAMES[this.currentMonth];
    if (yearCount) yearCount.innerHTML = `${this.currentYear} &bull; ${count} EVENT${count !== 1 ? 'S' : ''}`;

    this.renderGrid();
    this.renderList(events);
    this.renderLegend();
  }

  /* ─── FILTER (swiper pills, same style as events-list) ─── */

  renderFilter() {
    const container = this.root.querySelector('#ecal-tag-filters');
    const wrapper = this.root.querySelector('#ecal-tag-filters-wrapper');
    const swiperWrapper = container?.querySelector('.board-finder__swiper-wrapper');
    if (!container || !swiperWrapper) return;

    if (this.allCategories.length === 0) {
      if (wrapper) wrapper.style.display = 'none';
      return;
    }

    if (wrapper) wrapper.style.display = 'block';

    const sortedCategories = [...this.allCategories].sort();
    swiperWrapper.innerHTML = [
      '<div class="swiper-slide selected" data-filter="all"><div class="board-style-slide"><span>Alla</span></div></div>',
      ...sortedCategories.map(cat => {
        const slug = cat.toLowerCase().replace(/\s+/g, '-');
        return `<div class="swiper-slide" data-filter="${slug}" data-category="${cat}"><div class="board-style-slide"><span>${cat}</span></div></div>`;
      })
    ].join('');

    this.initializeFilterSwiper();

    container.querySelectorAll('.swiper-slide').forEach(slide => {
      slide.addEventListener('click', () => {
        container.querySelectorAll('.swiper-slide').forEach(s => s.classList.remove('selected'));
        slide.classList.add('selected');

        const filter = slide.dataset.filter;
        if (filter === 'all') {
          this.activeCategories = new Set(this.allCategories);
        } else {
          const cat = slide.dataset.category;
          this.activeCategories = new Set([cat]);
        }
        this.updateView();
      });
    });
  }

  initializeFilterSwiper() {
    const container = this.root.querySelector('#ecal-tag-filters');
    if (!container) return;

    if (this._filterResizeHandler) {
      window.removeEventListener('resize', this._filterResizeHandler);
      this._filterResizeHandler = null;
    }

    if (this.filterSwiper) {
      this.filterSwiper.destroy(true, true);
      this.filterSwiper = null;
    }

    const swiperEl = container.querySelector('.board-finder__swiper');
    if (!swiperEl || typeof Swiper === 'undefined') return;

    if (window.innerWidth >= 990) return;

    this.filterSwiper = new Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 10,
      navigation: {
        nextEl: container.querySelector('.swiper-button-next'),
        prevEl: container.querySelector('.swiper-button-prev'),
      },
    });

    this._filterResizeHandler = () => {
      clearTimeout(this._filterResizeTimer);
      this._filterResizeTimer = setTimeout(() => this.initializeFilterSwiper(), 250);
    };
    window.addEventListener('resize', this._filterResizeHandler);
  }

  renderLegend() {
    const el = this.root.querySelector('#ecal-legend');
    if (!el) return;

    const visibleCats = new Set();
    this.eventsForMonth(this.currentYear, this.currentMonth).forEach((ev) =>
      ev.categories?.forEach((c) => {
        if (this.activeCategories.has(c)) visibleCats.add(c);
      }),
    );

    if (visibleCats.size === 0) {
      el.innerHTML = '';
      return;
    }

    el.innerHTML = [...visibleCats]
      .sort()
      .map(
        (cat) =>
          `<span class="events-cal__legend-item"><span class="events-cal__legend-dot" style="background:${this.categoryColor(cat)}"></span>${cat}</span>`,
      )
      .join('');
  }

  /* ─── CALENDAR GRID ─── */

  isoWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  }

  renderGrid() {
    const grid = this.root.querySelector('#ecal-grid');
    if (!grid) return;

    const year = this.currentYear;
    const month = this.currentMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    let startDow = firstDay.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;

    const prevMonthLast = new Date(year, month, 0).getDate();

    const allDays = [];

    for (let i = startDow - 1; i >= 0; i--) {
      const d = prevMonthLast - i;
      const pm = month === 0 ? 11 : month - 1;
      const py = month === 0 ? year - 1 : year;
      allDays.push({ day: d, year: py, month: pm, other: true, today: false });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const isToday =
        d === this.today.getDate() && month === this.today.getMonth() && year === this.today.getFullYear();
      allDays.push({ day: d, year, month, other: false, today: isToday });
    }

    const remaining = allDays.length % 7 === 0 ? 0 : 7 - (allDays.length % 7);
    for (let d = 1; d <= remaining; d++) {
      const nm = month === 11 ? 0 : month + 1;
      const ny = month === 11 ? year + 1 : year;
      allDays.push({ day: d, year: ny, month: nm, other: true, today: false });
    }

    const cells = [];

    cells.push('<div class="events-cal__week-header caption">V</div>');
    EventsCalendar.WEEKDAYS.forEach((wd) => {
      cells.push(`<div class="events-cal__weekday">${wd}</div>`);
    });

    for (let i = 0; i < allDays.length; i++) {
      if (i % 7 === 0) {
        const d = allDays[i];
        const wn = this.isoWeekNumber(new Date(d.year, d.month, d.day));
        cells.push(`<div class="events-cal__week-number caption">${wn}</div>`);
      }
      const d = allDays[i];
      const dayEvents = this.eventsForDay(d.year, d.month, d.day);
      cells.push(this.renderDayCell(d.day, d.other, d.today, dayEvents));
    }

    grid.innerHTML = cells.join('');

    grid.querySelectorAll('.events-cal__day--has-events').forEach((cell) => {
      cell.addEventListener('click', () => {
        const dayNum = parseInt(cell.dataset.day);
        const isOther = cell.classList.contains('events-cal__day--other-month');
        if (isOther) return;
        const targetId = `ecal-day-marker-${dayNum}`;
        const marker = document.getElementById(targetId);
        if (marker) marker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  renderDayCell(day, isOtherMonth, isToday, events) {
    const hasEvents = events.length > 0;
    let classes = 'events-cal__day';
    if (isOtherMonth) classes += ' events-cal__day--other-month';
    if (isToday) classes += ' events-cal__day--today';
    if (hasEvents) classes += ' events-cal__day--has-events';

    const dots = events.map((ev) => {
      const cat = ev.categories?.[0] || '';
      const color = cat ? this.categoryColor(cat) : 'var(--cal-dot-default)';
      return `<span class="events-cal__day-dot" style="background:${color}" title="${ev.title}"></span>`;
    });

    const uniqueDots = [];
    const seenColors = new Set();
    events.forEach((ev) => {
      const cat = ev.categories?.[0] || '';
      const color = cat ? this.categoryColor(cat) : 'var(--cal-dot-default)';
      if (!seenColors.has(color)) {
        seenColors.add(color);
        uniqueDots.push(`<span class="events-cal__day-dot" style="background:${color}"></span>`);
      }
    });

    if (events.length > 3) {
      const countBadge = `<span class="events-cal__day-dot" style="background:var(--cal-dot-default);width:auto;border-radius:3px;padding:0 3px;font-size:8px;font-weight:700;color:var(--cal-bg);line-height:7px;">${events.length}</span>`;
      uniqueDots.push(countBadge);
    }

    return `
      <div class="${classes}" data-day="${day}">
        <span class="events-cal__day-number">${day}</span>
        <div class="events-cal__day-dots">${uniqueDots.join('')}</div>
      </div>
    `;
  }

  /* ─── EVENT LIST ─── */

  renderList(events) {
    const listEl = this.root.querySelector('#ecal-list');
    const headerEl = this.root.querySelector('#ecal-list-header');
    if (!listEl) return;

    const monthName = EventsCalendar.MONTH_NAMES[this.currentMonth];

    if (events.length === 0) {
      if (headerEl) headerEl.textContent = `${monthName} ${this.currentYear}`;
      listEl.innerHTML = `<p class="events-cal__list-empty">Inga event den här månaden.</p>`;
    } else {
      if (headerEl) headerEl.textContent = `${events.length} event i ${monthName} ${this.currentYear}`;

      const grouped = this.groupByDay(events);

      listEl.innerHTML = grouped
        .map((group) => {
          return group.events
            .map((ev, idx) => {
              const markerId = idx === 0 ? `id="ecal-day-marker-${group.day}"` : '';
              return this.renderCard(ev, markerId);
            })
            .join('');
        })
        .join('');
    }

    const nextMonth = this.currentMonth === 11 ? 0 : this.currentMonth + 1;
    const nextYear = this.currentMonth === 11 ? this.currentYear + 1 : this.currentYear;
    const nextMonthName = EventsCalendar.MONTH_NAMES[nextMonth];

    listEl.insertAdjacentHTML('beforeend', `
      <button class="events-cal__next-month-btn" aria-label="Visa ${nextMonthName} ${nextYear}">
        <span>${nextMonthName} ${nextYear}</span>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
      </button>
    `);

    listEl.querySelector('.events-cal__next-month-btn').addEventListener('click', () => {
      this.changeMonth(1);
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  groupByDay(events) {
    const map = new Map();
    events.forEach((ev) => {
      const effectiveStart = new Date(Math.max(ev.startDate, new Date(this.currentYear, this.currentMonth, 1)));
      const day = effectiveStart.getDate();
      if (!map.has(day)) map.set(day, []);
      map.get(day).push(ev);
    });

    return [...map.entries()].sort(([a], [b]) => a - b).map(([day, evs]) => ({ day, events: evs }));
  }

  renderCard(ev, markerId = '') {
    const now = new Date();
    const isPast = ev.endDate < now;
    const pastClass = isPast ? ' events-cal__card--past' : '';

    const dayName = ['SÖN', 'MÅN', 'TIS', 'ONS', 'TOR', 'FRE', 'LÖR'][ev.startDate.getDay()];
    const dayNum = ev.startDate.getDate();
    const monthShort = EventsCalendar.MONTH_SHORT[ev.startDate.getMonth()].toUpperCase();
    const dateDisplay = this.formatDateRange(ev.startDate, ev.endDate);
    const tag = ev.categories?.[0] || 'Event';
    const tagColor = this.categoryColor(tag);

    let imageHtml = '';
    if (ev.image) {
      const ratio = ev.imageWidth && ev.imageHeight ? ev.imageWidth / ev.imageHeight : 1;
      let sizeClass = 'events-cal__card-image--square';
      if (ratio > 1.2) sizeClass = 'events-cal__card-image--landscape';
      else if (ratio < 0.8) sizeClass = 'events-cal__card-image--portrait';
      imageHtml = `
        <div class="events-cal__card-image ${sizeClass}">
          <img src="${ev.image}" alt="${ev.imageAlt || ev.title}" loading="lazy">
        </div>`;
    }

    return `
      <a href="/pages/events/${ev.handle}" class="events-cal__card${pastClass}" ${markerId}>
        <div class="events-cal__card-date">
          <div class="events-cal__card-date-day h2">${dayNum}</div>
          <div class="events-cal__card-date-info caption">${dayName}<br>${monthShort}</div>
          <span class="events-cal__card-tag caption" style="background:${tagColor}">${tag}</span>
        </div>
        ${imageHtml}
        <div class="events-cal__card-body">
          <h3 class="events-cal__card-title h3">${ev.title}</h3>
          <div class="events-cal__card-meta caption">${dateDisplay}</div>
          ${ev.address ? `<div class="events-cal__card-location caption"><svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.5 8.33C17.5 14.17 10 19.17 10 19.17S2.5 14.17 2.5 8.33a7.5 7.5 0 1115 0z"/><circle cx="10" cy="8.33" r="2.5"/></svg>${ev.address}</div>` : ''}
        </div>
      </a>
    `;
  }

  formatDateRange(start, end) {
    const months = EventsCalendar.MONTH_SHORT;
    const days = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
    const sameDay = start.toDateString() === end.toDateString();
    const hours = (end - start) / (1000 * 60 * 60);

    if (sameDay && hours > 0 && hours <= 12) {
      const t = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
      const te = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
      return `${days[start.getDay()]}, ${start.getDate()} ${months[start.getMonth()]} • ${t}–${te}`;
    }
    if (sameDay) {
      return `${days[start.getDay()]}, ${start.getDate()} ${months[start.getMonth()]}`;
    }
    return `${start.getDate()} ${months[start.getMonth()]} — ${end.getDate()} ${months[end.getMonth()]}`;
  }
}

/* ─── INIT ─── */
function initEventsCalendar() {
  const root = document.querySelector('[data-events-calendar]');
  if (root) new EventsCalendar(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initEventsCalendar);
} else {
  initEventsCalendar();
}
